// 마이페이지.
// 작업계획서 4.6: 사용자 정보, 적립금, 등급, 쿠폰, 주문 조회, 주소관리, 공지, 1:1 문의(전화/카카오), 알림 설정 등.
// Phase 4 에서 각 카드/링크 화면이 채워진다.

import { Link, useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronRight,
  ClipboardList,
  Coins,
  Gamepad2,
  Headset,
  LogOut,
  MapPin,
  Ticket,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { formatPoints } from "@oo/shared";
import { useAuthStore } from "@/stores/authStore.ts";
import { logoutSession } from "@/features/auth/authApi.ts";
import { getPoints } from "@/features/points/pointsApi.ts";
import { getMembership } from "@/features/points/membershipApi.ts";
import { getNotifications } from "@/features/notifications/notificationsApi.ts";
import { getGameConfig } from "@/features/games/gamesApi.ts";
import PushToggle from "@/features/push/PushToggle.tsx";

// 빠른 진입 메뉴 정의. 각 행의 아이콘은 라벨과 함께 노출하므로 aria-hidden 으로 둔다.
const MENU: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }> = [
  { to: "/me/notifications", label: "알림", icon: Bell },
  { to: "/me/points", label: "적립금", icon: Coins },
  { to: "/me/coupons", label: "쿠폰함", icon: Ticket },
  { to: "/me/orders", label: "주문 내역", icon: ClipboardList },
  { to: "/me/addresses", label: "배송지 관리", icon: MapPin },
  // 카드 등록(빌링키) 기능은 폐지됐다(KPN 정기결제 MID 미발급). 결제수단은 주문할 때 그때그때 고른다.
  { to: "/me/support", label: "고객센터 / 1:1 문의", icon: Headset },
];

// 마이페이지 컴포넌트. 골격 단계에서는 본인 정보 요약과 로그아웃만 노출한다.
export default function MyPage() {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const clearSession = useAuthStore((s) => s.clearSession);
  // 적립금 잔액 + 회원 등급 (마이페이지 요약 표시).
  const pointsQuery = useQuery({ queryKey: ["points"], queryFn: getPoints });
  const membershipQuery = useQuery({ queryKey: ["membership"], queryFn: getMembership });
  const notificationsQuery = useQuery({ queryKey: ["notifications"], queryFn: getNotifications });
  const unread = notificationsQuery.data?.unread ?? 0;
  // 게임 기능은 운영설정(games_enabled)으로 켜진 경우에만 메뉴에 노출(킬스위치 게이팅).
  const gameConfigQuery = useQuery({ queryKey: ["game-config"], queryFn: getGameConfig });
  const menuItems: ReadonlyArray<{ to: string; label: string; icon: LucideIcon }> = gameConfigQuery
    .data?.gamesEnabled
    ? [...MENU, { to: "/me/games", label: "게임", icon: Gamepad2 }]
    : MENU;

  async function onLogout() {
    if (session?.refreshToken) {
      // 서버 측 무효화 실패해도 클라이언트는 즉시 세션을 비워야 한다.
      // 네트워크 오류 등으로 logout 호출이 실패해도 UX 가 막히지 않게 catch 후 무시.
      try {
        await logoutSession(session.refreshToken);
      } catch {
        // 의도적 무시
      }
    }
    clearSession();
    navigate("/", { replace: true });
  }

  return (
    <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
      <h1 className="text-2xl font-bold">마이페이지</h1>
      <div className="flex flex-col gap-2 rounded-md border border-ink-200 p-4">
        <p className="text-base">
          <span className="text-ink-600">표시 이름</span>{" "}
          <span className="font-medium">{session?.user.displayName ?? "(미지정)"}</span>
        </p>
        {/* 회원 등급 사용(tierEnabled)일 때만 등급을 노출(개선 4). 미사용/로딩 중에는 숨긴다. */}
        {membershipQuery.data?.tierEnabled ? (
          <p className="text-base">
            <span className="text-ink-600">등급</span>{" "}
            <span className="font-medium">{membershipQuery.data.tier.name}</span>
          </p>
        ) : null}
        <p className="text-base">
          <span className="text-ink-600">적립금</span>{" "}
          <span className="font-bold tabular-nums">
            {pointsQuery.isLoading ? "..." : formatPoints(pointsQuery.data?.balance ?? 0)}
          </span>
        </p>
        {/* 등급 산정 안내 + 다음 등급까지 (등급 정책이 설정된 경우에만) */}
        {membershipQuery.data && membershipQuery.data.nextTier ? (
          <p className="text-xs text-ink-600">
            최근 {membershipQuery.data.windowMonths}개월 구매 실적{" "}
            {membershipQuery.data.windowSpentKrw.toLocaleString()}원 · 다음 등급(
            <span className="font-medium text-ink-700">{membershipQuery.data.nextTier.name}</span>)까지{" "}
            {membershipQuery.data.nextTier.remainingKrw.toLocaleString()}원
          </p>
        ) : null}
      </div>
      {/* 빠른 진입 메뉴. 데이터 기반으로 아이콘 + 라벨 + 셰브론을 일관 렌더. */}
      <ul className="flex flex-col gap-1">
        {menuItems.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <Link
              to={to}
              className="flex h-14 items-center gap-3 rounded-md border border-ink-200 px-4 hover:bg-ink-50"
            >
              <Icon size={20} aria-hidden="true" className="text-ink-600" />
              <span className="flex flex-1 items-center gap-2 text-base">
                {label}
                {/* 알림 행에만 미확인 개수 배지 */}
                {to === "/me/notifications" && unread > 0 ? (
                  <span className="rounded-full bg-brand-600 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {unread}
                  </span>
                ) : null}
              </span>
              <ChevronRight size={18} aria-hidden="true" className="text-ink-400" />
            </Link>
          </li>
        ))}
      </ul>
      {/* 주문 알림(웹 푸시) 토글 */}
      <PushToggle />

      <button
        type="button"
        onClick={onLogout}
        className="mt-4 inline-flex h-12 items-center justify-center gap-2 rounded-md border border-ink-300 text-base font-medium hover:bg-ink-100"
      >
        <LogOut size={18} aria-hidden="true" />
        로그아웃
      </button>
    </main>
  );
}
